/**
 * The full analysis of a version of the open document (DESIGN UD-27, VERSION-CHAIN-DESIGN §4), in a worker of its own, so the
 * open document is not replaced or queued behind it: the worker holds the document's find and exports, and ending this one
 * (cancel) leaves it untouched. The worker answers an `analyse` request that carries a version tag with the whole result,
 * tagged with the version's name and `meta.version`; nothing is stored.
 */
import type { VersionTag } from '../../shared/protocol.ts';
import type { ViewModel } from '../../shared/viewmodel.ts';
import { WorkerClient } from '../worker-client.ts';

let own: WorkerClient | null = null;

/**
 * Analyses `text` as version `tag.n` of the document `tag.root` and resolves with its view model. `onProgress` gets the
 * overall fraction (0 to 1) by the stage weights. Aborting `signal` ends the worker and rejects with an `AbortError`.
 */
export function analyseVersionInWorker(
	text: string,
	tag: VersionTag,
	onProgress?: (fraction: number) => void,
	signal?: AbortSignal
): Promise<ViewModel> {
	if (!own) own = new WorkerClient();
	const client = own;
	const id = client.id();
	return new Promise<ViewModel>((resolve, reject) => {
		let off: () => void = () => undefined;
		const stop = () => {
			off();
			signal?.removeEventListener('abort', onAbort);
		};
		const onAbort = () => {
			stop();
			// the analysis is synchronous CPU work inside the engine: only ending the worker stops it
			client.restart();
			reject(Object.assign(new Error('The analysis was cancelled.'), { name: 'AbortError' }));
		};
		if (signal?.aborted) return onAbort();
		signal?.addEventListener('abort', onAbort);
		off = client.subscribe((m) => {
			if (m.id !== id) return;
			if (m.type === 'progress') onProgress?.(Math.min(1, m.pct / 100));
			else if (m.type === 'result') {
				stop();
				resolve(m.payload);
			} else if (m.type === 'error') {
				stop();
				reject(new Error(m.message));
			} else if (m.type === 'cancelled') {
				stop();
				reject(Object.assign(new Error('The analysis was cancelled.'), { name: 'AbortError' }));
			}
		});
		client.onCrash = () => {
			stop();
			reject(new Error('The analysis worker stopped unexpectedly.'));
		};
		client.send({
			type: 'analyse',
			id,
			source: { kind: 'text', text, name: tag.name },
			version: tag,
		});
	});
}
